import React, { useEffect, useState } from 'react';
import * as Styles from './styles';

function Admin() {
  const [instructors, setInstructors] = useState([]);
  
  // State to manage the active instructor detail modal overlay
  const [selectedInstructor, setSelectedInstructor] = useState(null);

  // --- NEW: Admin Messaging State ---
  const [adminMessageText, setAdminMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const fetchInstructors = () => {
    const token = localStorage.getItem('adminToken'); // Or whatever key your login sets

    fetch('/api/instructors', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setInstructors(data);
      })
      .catch(err => console.error("Fetch error:", err));
  };

  // --- SECURITY CHECK ON MOUNT ---
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      // If not logged in, kick them out immediately
      window.location.href = '/';
      return;
    }
    fetchInstructors();
  }, []);

  // --- Action Handlers ---
  const handleDeleteInstructor = async (instructorId, instructorName) => {
    if (!window.confirm(`⚠️ WARNING: Are you sure you want to delete instructor "${instructorName}"?\n\nThis will permanently delete ALL associated students, lesson logs, and bookings to free up database space. This cannot be undone.`)) {
        return;
    }

    try {
        const res = await fetch(`/api/admin/instructors/${instructorId}`, {
            method: 'DELETE'
        });
        const data = await res.json();

        if (res.ok) {
            alert("✅ Instructor and all associated data successfully removed.");
            setSelectedInstructor(null);
            fetchInstructors();
        } else {
            alert(`❌ Error: ${data.error || "Could not delete instructor."}`);
        }
    } catch (err) {
        console.error("Delete instructor network error:", err);
        alert("❌ Server connection lost.");
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await fetch(`/api/instructors/${id}/approve`, { method: 'PATCH' });
      const data = await response.json();
      
      if (response.ok) {
        alert(`✅ Approved successfully!\n\n📧 A secure password setup link has been emailed to the instructor.`);
        setSelectedInstructor(null);
      } else {
        alert(`❌ Error: ${data.error || "Could not approve instructor."}`);
      }
    } catch (err) {
      console.error("Approval error:", err);
      alert("❌ Server connection lost.");
    }
    fetchInstructors();
  };

  const handleReject = async (id) => {
    try {
      const response = await fetch(`/api/instructors/${id}/reject`, { method: 'PATCH' });
      if (response.ok) {
        alert("🛑 Instructor Rejected.");
        setSelectedInstructor(null);
      }
    } catch (err) {
      console.error("Rejection error:", err);
    }
    fetchInstructors();
  };

  const handleReset = async (id) => {
    try {
      const response = await fetch(`/api/instructors/${id}/reset`, { method: 'PATCH' });
      if (response.ok) {
        alert("🔄 Instructor reset back to pending state.");
        setSelectedInstructor(null);
      }
    } catch (err) {
      console.error("Reset error:", err);
    }
    fetchInstructors();
  };

  // --- NEW: Send Admin Notice Handler ---
  const handleSendAdminNotice = async (instructorId) => {
    if (!adminMessageText.trim()) {
      alert("Please enter a message to send.");
      return;
    }

    try {
      setSendingMessage(true);
      const res = await fetch('/api/messages/admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instructor_id: instructorId,
          sender_role: 'admin',
          message_text: adminMessageText.trim()
        })
      });

      if (res.ok) {
        alert("✅ Admin notice successfully dispatched to the instructor's portal inbox!");
        setAdminMessageText('');
      } else {
        alert("❌ Failed to send admin notice.");
      }
    } catch (err) {
      console.error("Admin message error:", err);
      alert("❌ Server connection error.");
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <div style={Styles.containerStyle}>
      
      {/* --- HEADER CONTAINER --- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
        <h2 style={{ color: '#111111', margin: 0, fontSize: '1.8rem', fontWeight: '800', letterSpacing: '-0.02em' }}>Admin Dashboard</h2>
        <button 
          onClick={() => {
            localStorage.clear(); 
            window.location.href = '/'; 
          }} 
          style={rejectButtonStyle}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#c53030'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#e53e3e'}
        >
          Logout 🚪
        </button>
      </div>
      
      {/* --- MAIN DATA TABLE CONTAINER --- */}
      <div style={{ width: '100%', maxWidth: '100%', backgroundColor: '#ffffff', borderRadius: '12px', padding: '15px', border: '1px solid #cbd5e0', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', boxSizing: 'border-box', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', color: '#111111', minWidth: '600px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #cbd5e0', textAlign: 'left' }}>
              <th style={headerStyle}>Name</th>
              <th style={headerStyle}>Email</th>
              <th style={headerStyle}>Status</th>
              <th style={headerStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {instructors.map(inst => (
              <tr 
                key={inst.id} 
                style={{ ...rowStyle, cursor: 'pointer' }}
                onClick={async () => {
                  try {
                    const res = await fetch(`/api/instructors/${inst.id}`);
                    if (res.ok) {
                      const freshData = await res.json();
                      setSelectedInstructor(freshData);
                      setAdminMessageText(''); // Reset message box on open
                    }
                  } catch (err) {
                    console.error("Error grabbing active row tracking data, falling back:", err);
                    setSelectedInstructor(inst);
                  }
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <td style={cellStyle}>🔍 <strong style={{ color: '#0066cc' }}>{inst.name || "N/A"}</strong></td>
                <td style={cellStyle}>{inst.email || "N/A"}</td>
                <td style={{ 
                  ...cellStyle, 
                  color: inst.status === 'pending' ? '#d69e2e' : 
                         inst.status === 'active' ? '#2f855a' : '#e53e3e', 
                  fontWeight: 'bold' 
                }}>
                  {inst.status ? inst.status.toUpperCase() : 'PENDING'}
                </td>
                <td style={cellStyle} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {inst.status === 'pending' ? (
                      <>
                        <button onClick={() => handleApprove(inst.id)} style={approveButtonStyle}>
                          Approve
                        </button>
                        <button onClick={() => handleReject(inst.id)} style={rejectButtonStyle}>
                          Reject
                        </button>
                      </>
                    ) : (
                      <button onClick={() => handleReset(inst.id)} style={resetButtonStyle}>
                        Reset
                      </button>
                    )}
                    <button onClick={() => handleDeleteInstructor(inst.id, inst.name)} style={deleteButtonStyle}>
                      Delete 🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {instructors.length === 0 && <p style={{ textAlign: 'center', marginTop: '20px', color: '#4a5568' }}>No entries found.</p>}
      </div>

      {/* ================================================================= */}
      {/* STRUCTURED ONBOARDING AUDIT RECITATION DETAILED MODAL             */}
      {/* ================================================================= */}
      {selectedInstructor && (
        <div 
          style={modalOverlayStyle}
          onClick={() => setSelectedInstructor(null)} 
        >
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #cbd5e0', paddingBottom: '15px', marginBottom: '20px' }}>
              <div>
                <h2 style={{ margin: 0, color: '#111111', fontSize: '18px' }}>📋 Application Profile Dossier</h2>
                <span style={{ fontSize: '11px', color: '#4a5568' }}>Reference ID: #{selectedInstructor.id}</span>
              </div>
              <button onClick={() => setSelectedInstructor(null)} style={closeModalBtnStyle}>✕</button>
            </div>

            {/* Layout Structural Matrix Splitting Info columns */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100%, 1fr))', gap: '15px', textAlign: 'left' }}>

              {/* Column 1: Core Account Bio Credentials */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <span style={metaLabelStyle}>Full Name</span>
                  <div style={metaValueStyle}>{selectedInstructor.name}</div>
                </div>

                {/* 🎂 Date of Birth Display */}
                <div>
                  <span style={metaLabelStyle}>🎂 Date of Birth</span>
                  <div style={metaValueStyle}>
                    {selectedInstructor.date_of_birth ? new Date(selectedInstructor.date_of_birth).toLocaleDateString('en-GB') : 'Not Provided'}
                  </div>
                </div>

                <div>
                  <span style={metaLabelStyle}>🛡️ DVSA Registration Badge Identifier</span>
                  <div style={{ 
                    ...metaValueStyle, 
                    background: '#fffaf0', 
                    border: '1px solid #d69e2e', 
                    color: '#b7791f', 
                    fontWeight: 'bold',
                    fontSize: '14px',
                    wordBreak: 'break-all'
                  }}>
                    🪪 {selectedInstructor.dvsa_badge_number || 'PENDING VERIFICATION / NOT SUPPLIED'}
                  </div>
                </div>

                <div>
                  <span style={metaLabelStyle}>Primary Email Address</span>
                  <div style={{ ...metaValueStyle, wordBreak: 'break-all' }}>✉️ {selectedInstructor.email}</div>
                </div>
                <div>
                  <span style={metaLabelStyle}>Contact Telephone Number</span>
                  <div style={metaValueStyle}>📞 {selectedInstructor.phone || 'Not Provided'}</div>
                </div>
              </div>

              {/* Column 2: Geographic Travel Radiuses */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <span style={metaLabelStyle}>Registered Business / Home Base Address</span>
                  <div style={{ ...metaValueStyle, wordBreak: 'break-all' }}>🏠 {selectedInstructor.address || 'Not Provided'}</div>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '120px' }}>
                    <span style={metaLabelStyle}>Base Postcode</span>
                    <div style={metaValueStyle}>📮 {selectedInstructor.postcode || 'N/A'}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '120px' }}>
                    <span style={metaLabelStyle}>Max Travel Radius</span>
                    <div style={metaValueStyle}>🗺️ {selectedInstructor.travel_radius_miles || 5} Miles</div>
                  </div>
                </div>
              </div>

            </div>

            {/* Fleet Capability Certifications Badges row */}
            <div style={{ textAlign: 'left', marginTop: '15px', paddingTop: '15px', borderTop: '1px dashed #cbd5e0' }}>
              <span style={metaLabelStyle}>Verified Fleet Training Capabilities</span>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {(() => {
                  try {
                    const parsed = typeof selectedInstructor.transmission_type === 'string'
                      ? JSON.parse(selectedInstructor.transmission_type)
                      : selectedInstructor.transmission_type;
                    
                    const categoriesArray = Array.isArray(parsed) ? parsed : [];
                    
                    if (categoriesArray.length === 0) {
                      return <span style={badgeStyle}>Car (Manual - Default Fleet)</span>;
                    }

                    return categoriesArray.map(cat => (
                      <span key={cat} style={badgeStyle}>🚀 {cat}</span>
                    ));
                  } catch (e) {
                    return <span style={badgeStyle}>🚀 {selectedInstructor.transmission_type || 'Car (Manual)'}</span>;
                  }
                })()}
              </div>
            </div>

            {/* --- NEW: ADMIN-TO-INSTRUCTOR DIRECT NOTICE BOX --- */}
            <div style={{ textAlign: 'left', marginTop: '15px', paddingTop: '15px', borderTop: '1px dashed #cbd5e0' }}>
              <span style={metaLabelStyle}>🏛️ Send Admin Notice / Direct Message</span>
              <div style={{ marginTop: '8px', background: '#f8f9fa', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e0' }}>
                <textarea 
                  rows={3}
                  value={adminMessageText}
                  onChange={e => setAdminMessageText(e.target.value)}
                  placeholder="Type an announcement or instruction for this instructor..."
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '12px', boxSizing: 'border-box', background: '#fff', color: '#111' }}
                />
                <button 
                  type="button"
                  onClick={() => handleSendAdminNotice(selectedInstructor.id)}
                  disabled={sendingMessage}
                  style={{ marginTop: '8px', background: '#0066cc', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', width: '100%' }}
                >
                  {sendingMessage ? 'Sending...' : 'Dispatch Notice to Instructor 🚀'}
                </button>
              </div>
            </div>

            {/* Bottom Modal Actions Quick Toolbar */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
              <button 
                onClick={() => handleDeleteInstructor(selectedInstructor.id, selectedInstructor.name)} 
                style={{ ...deleteButtonStyle, width: '100%', marginBottom: '5px' }}
              >
                🗑️ Purge Instructor & All Data
              </button>

              <div style={{ display: 'flex', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                {selectedInstructor.status === 'pending' ? (
                  <>
                    <button onClick={() => handleApprove(selectedInstructor.id)} style={{ ...approveButtonStyle, flex: 1, padding: '10px' }}>
                      ✓ Approve
                    </button>
                    <button onClick={() => handleReject(selectedInstructor.id)} style={{ ...rejectButtonStyle, flex: 1, padding: '10px' }}>
                      ✕ Reject
                    </button>
                  </>
                ) : (
                  <button onClick={() => handleReset(selectedInstructor.id)} style={{ ...resetButtonStyle, flex: 1, padding: '10px' }}>
                    🔄 Reset to Pending
                  </button>
                )}
                <button onClick={() => setSelectedInstructor(null)} style={{ flex: 1, padding: '10px', background: '#e2e8f0', color: '#111111', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                  Close
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

// --- Shared Internal Styles ---
const headerStyle = {
  padding: '12px 10px',
  color: '#0066cc',
  fontSize: '0.85rem',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  fontWeight: '700',
  whiteSpace: 'nowrap'
};

const cellStyle = {
  padding: '12px 10px',
  borderBottom: '1px solid #e2e8f0',
  color: '#111111',
  fontSize: '13px',
  wordBreak: 'break-word'
};

const rowStyle = {
  borderBottom: '1px solid #e2e8f0',
  transition: 'background-color 0.1s'
};

const approveButtonStyle = {
  padding: '6px 10px',
  backgroundColor: '#2f855a', 
  color: '#ffffff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '12px',
  whiteSpace: 'nowrap'
};

const rejectButtonStyle = {
  padding: '6px 10px',
  backgroundColor: '#e53e3e',
  color: '#ffffff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '12px',
  whiteSpace: 'nowrap'
};

const resetButtonStyle = {
  padding: '6px 10px',
  backgroundColor: '#4a5568',
  color: '#ffffff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '12px',
  whiteSpace: 'nowrap'
};

const deleteButtonStyle = {
  padding: '6px 10px',
  backgroundColor: '#9b2c2c',
  color: '#ffffff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '12px',
  whiteSpace: 'nowrap'
};

const modalOverlayStyle = { 
  position: 'fixed', 
  top: 0, 
  left: 0, 
  width: '100vw', 
  height: '100vh', 
  backgroundColor: 'rgba(0,0,0,0.5)', 
  display: 'flex', 
  justifyContent: 'center', 
  alignItems: 'center', 
  padding: '10px', 
  boxSizing: 'border-box',
  overflowY: 'auto', 
  zIndex: 9999 
};

const modalCardStyle = { 
  background: '#ffffff', 
  border: '1px solid #cbd5e0', 
  borderRadius: '12px', 
  padding: '15px', 
  maxWidth: '600px', 
  width: '100%', 
  boxSizing: 'border-box',
  boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
  maxHeight: '90vh',
  overflowY: 'auto',
  color: '#111111' 
};


const closeModalBtnStyle = { background: 'none', border: 'none', color: '#4a5568', fontSize: '20px', cursor: 'pointer', fontWeight: 'bold' };
const metaLabelStyle = { fontSize: '11px', color: '#4a5568', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };
const metaValueStyle = { padding: '8px 10px', background: '#f8f9fa', border: '1px solid #cbd5e0', borderRadius: '6px', color: '#111111', fontSize: '13px', fontWeight: '500' };
const badgeStyle = { background: '#0066cc', color: '#ffffff', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' };

export default Admin;